/**
 * Vertrag der API-Grenze /api/v1.
 *
 * Diese Typen werden von Backend und Frontend gemeinsam benutzt. Das ist der
 * Zweck des Monorepos (HOMEBASE_ARCHITECTURE.md, Abschnitt 18.1): der Vertrag
 * wird einmal definiert, nicht zweimal beschrieben.
 */
/** Ein Artikel in der Listenansicht. */
/**
 * WARUM EIN ARTIKEL NICHT BESTELLFAEHIG IST -- sprachneutral.
 *
 * Die Reihenfolge ist die der Abhilfe, nicht die der Schwere: wer die
 * Nummer vergibt, hat danach ein Modell zu haengen, und erst dann ist
 * die Ausfuehrung zu bestimmen.
 */
export const ORDERABILITY_BLOCKERS = [
    /** Eine Platzhalternummer ist keine Artikelnummer. */
    "number_placeholder",
    /** Ohne Modell ist nicht bestimmt, WAS bestellt wird. */
    "no_model_link",
    /** Das Modell haengt dran, die Ausfuehrung ist nicht vollstaendig bestimmt. */
    "variant_incomplete",
    /** `inaktiv` oder „noch nicht freigegeben" -- Entscheidungen gegen eine Bestellung. */
    "lifecycle_blocks",
    /** Ohne Lieferantenbeziehung gibt es niemanden, bei dem man bestellt. */
    "no_supplier_link",
];
/* ── Datenanforderungsprofil (Produktdaten 001 A) ───────────────────────── */
/** Wozu eine erwartete Information gebraucht wird. */
export const REQUIREMENT_PURPOSES = ["stammdaten", "text", "compliance", "kalkulation", "logistik"];
/**
 * Das Gewicht einer Anforderung -- zwei Werte, und „optional" ist keiner.
 *
 * Was lediglich moeglich ist, steht im Merkmalskatalog. Eine Profilzeile ohne
 * Wirkung waere eine Zeile, die gepflegt werden will und nichts bewirkt.
 */
export const REQUIREMENT_WEIGHTS = ["erwartet", "empfohlen"];
export const REQUIREMENT_SUBJECT_KINDS = ["property", "article_field", "structured"];
/**
 * Der Zustand einer erwarteten Information an einem Gegenstand.
 *
 * KEINER davon wird gespeichert. Alle fuenf entstehen aus dem, was ohnehin da
 * ist -- Wert, Qualitaetsstand, laufende Informationsanfrage. Eine
 * gespeicherte Zustandsspalte waere am Tag nach der naechsten Bestaetigung
 * falsch.
 *
 * Die Rangfolge bei Mehrdeutigkeit: `nicht_anwendbar` schlaegt alles (ein
 * Mensch hat entschieden), ein vorhandener Wert schlaegt eine laufende Frage
 * (die Antwort ist da, auch wenn niemand die Aufgabe geschlossen hat).
 */
export const REQUIREMENT_STATES = [
    "nicht_anwendbar", "vorhanden_belastbar", "vorhanden_unbestaetigt",
    "angefragt", "fehlt",
];
/** Woher der Wert kommt, der eine Anforderung erfuellt. */
export const REQUIREMENT_SOURCES = ["eigen", "modell", "achse", "abgeleitet", "feld"];
/**
 * Die Arbeitsbereiche eines Artikels -- die Orte, an denen etwas GETAN wird.
 *
 * Bewusst ohne "Uebersicht": dort wird nichts bearbeitet. Ein Hinweis auf
 * offene Arbeit traegt immer den Bereich, in dem er bearbeitet werden kann;
 * ein Hinweis mit dem Ziel "Uebersicht" waere ein Hinweis ohne Handgriff.
 *
 * EINKAUF UND LOGISTIK SIND GETRENNT (V2). Sie waren einer, weil beides an
 * derselben Supplier-Beziehung haengt -- eine Herkunft aus dem Datenmodell,
 * nicht aus der Sache. Fachlich sind es zwei Klassen: Kartonmasse, Gewicht
 * und CBM sind fuer Vertrieb und Planung nuetzlich und nicht vertraulich;
 * Fertigungspartner, Supplier-Artikelnummer, MOQ und Konditionen sind es.
 * Wer beides in einem Bereich zeigt, kann das eine nicht freigeben, ohne das
 * andere mitzugeben.
 */
export const WORK_SECTIONS = [
    "technik", "logistik", "texte", "dokumente", "einkauf", "modell", "herkunft",
];
/**
 * Die Bereiche eines Produktes, wie die Subnavigation sie anbietet.
 *
 * HIER STAND "uebersicht" AN ERSTER STELLE.
 *
 * Sie war als kundensichere Erstansicht gedacht und wurde im Gebrauch etwas
 * anderes: eine zweite Technikansicht. Dieselben Lichtwerte, dieselbe
 * Elektrik, dieselben Masse -- nur in einer anderen Anordnung. Wer eine
 * Zahl suchte, musste erst entscheiden, in welchem der beiden Reiter er
 * nachsieht, und wer eine aenderte, musste an zwei Stellen pruefen, ob sie
 * ueberall richtig ankommt.
 *
 * Zwei Ansichten auf denselben Bestand sind keine Wahl, sondern eine Frage
 * zu viel. Die Uebersicht ist deshalb am 17.09.2026 entfallen; Technik ist
 * der erste fachliche Reiter. Ihr operativer Teil -- Bestand, Angebote,
 * Bestellung, Zulauf -- ist NICHT verschwunden, sondern in den Produktkopf
 * gewandert, wo er zu jedem Reiter sichtbar bleibt. Dort gehoert er hin: er
 * beschreibt den Artikel, nicht einen seiner Arbeitsbereiche.
 *
 * Die Liste ist die einzige Quelle fuer Reihenfolge und Umfang der
 * Subnavigation. Ein weiterer Bereich ist eine Zeile hier, keine Aenderung
 * an der Navigationskomponente.
 */
export const PRODUCT_SECTIONS = [...WORK_SECTIONS];
/**
 * Bereiche, die ohne eine bestimmte Faehigkeit gar nicht erst erscheinen.
 *
 * Ein ausgegrauter Reiter sagt "hier gibt es etwas, das du nicht sehen
 * darfst" -- das ist bereits eine Auskunft. Fehlt der Reiter, gibt es fuer
 * diesen Nutzer diesen Bereich nicht.
 *
 * Diese Deklaration steuert die OBERFLAECHE. Der eigentliche Schutz liegt im
 * Server: seit Migration 0015 liefert er die vertraulichen Angaben nur an
 * Traeger von `purchasing.read` aus. Beide Seiten nennen dasselbe Recht --
 * die Oberflaeche bietet nichts an, was der Server ohnehin nicht herausgibt,
 * und der Server verlaesst sich nicht darauf, dass sie es nicht tut.
 */
export const SECTION_CAPABILITY = {
    einkauf: "purchasing.read",
};
/**
 * DIE ABSCHNITTE DER ZENTRALEN PRODUKTAKTE.
 *
 * Die Reihenfolge ist NICHT frei gewaehlt: sie ist die Lesereihenfolge der
 * Masterpreisliste, mit der im Unternehmen seit Jahren gearbeitet wird. Wer
 * einen Artikel dort von links nach rechts durchgeht, kommt zuerst zur
 * Identitaet, dann zur Lichttechnik, zuletzt zum Verkaufspreis. Die Akte
 * folgt diesem Weg, damit niemand eine neue Ordnung lernen muss.
 *
 * EINE DURCHGEHENDE FLAECHE, KEINE ZEHN REITER. Die Abschnitte stehen
 * untereinander; die Sprungleiste bringt einen hin, ohne den Zusammenhang zu
 * zerschneiden. Das ist der Unterschied zur Arbeitsansicht (`WORK_SECTIONS`),
 * die bewusst jeweils EINEN Bereich zeigt: dort wird an einer Sache
 * gearbeitet, hier wird ein Artikel als Ganzes gepflegt.
 */
export const DOSSIER_SECTIONS = [
    /*
     * `varianten` steht an ZWEITER Stelle und nicht am Ende.
     *
     * Der Variantenraum ist eine Aussage ueber die Identitaet des Produkts --
     * „diesen Artikel gibt es auch mit 60°" gehoert neben die Artikelnummer und
     * nicht hinter den Listenpreis. Und er steht VOR den Merkmalen, weil er die
     * Frage beantwortet, die man sonst beim Lesen der Merkmale stellt: ist die
     * Angabe „120°" der einzige Wert oder nur der Standard?
     */
    "identitaet", "varianten", "lichttechnik", "stromtechnik", "konstruktion",
    "verpackung", "zoll", "einkauf", "zuschlaege",
    "importkalkulation", "verkaufspreis",
];
/**
 * Abschnitte, die ohne `purchasing.read` gar nicht erst erscheinen.
 *
 * Die letzten vier gehoeren zusammen: Einkaufspreis, Zuschlaege, Landed Cost
 * und Marge sind EINE vertrauliche Kette. Einen einzelnen davon freizugeben
 * hiesse, aus den uebrigen zurueckrechnen zu lassen -- aus Listenpreis und
 * Marge folgt der Einkaufspreis.
 *
 * Wie ueberall: das steuert die Oberflaeche. Der Schutz liegt im Server, der
 * fuer `GET /articles/:id/calculation` dasselbe Recht verlangt.
 */
export const DOSSIER_SECTION_CAPABILITY = {
    einkauf: "purchasing.read",
    zuschlaege: "purchasing.read",
    importkalkulation: "purchasing.read",
    verkaufspreis: "purchasing.read",
};
/**
 * Die fuenf fuer V1 freigegebenen Rollen.
 *
 * Bis zur Migration 0056 waren es zwei -- `admin` und `viewer`. Dass es
 * jetzt fuenf sind, kostet im Server KEINE einzige zusaetzliche Pruefung:
 * jede Schranke fragt ein RECHT ab und nie einen Rollennamen. Eine Rolle
 * ist ein Buendel, kein Sonderfall.
 *
 *   admin      vollstaendige Rechte einschliesslich Konten und Betrieb
 *   viewer     rein lesend; keine Beschaffung, keine Kalkulation
 *   product    Produktmanagement und Einkauf, ohne Systemverwaltung
 *   marketing  Texte und Dokumente, ohne Preise und Beschaffung
 *   connector  Datenbruecke DTE <-> HOMEBASE; liest breit, schreibt eng
 *
 * Im Datenbestand existieren aus dem urspruenglichen Entwurf ausserdem
 * `produktdaten` und `kalkulation`. Sie sind fachlich nicht freigegeben,
 * keinem Benutzer zugeordnet und tauchen deshalb hier nicht auf. Geloescht
 * werden sie nicht -- das entscheidet die Projektsteuerung, nicht der Code.
 */
export const ROLE_CODES = ["admin", "viewer", "product", "marketing", "connector"];
/**
 * Benannte Faehigkeiten.
 *
 * Der Zugangsschutz prueft ausschliesslich diese Codes -- nie den Rollennamen.
 * Eine dritte Rolle liesse sich damit spaeter zuordnen, ohne eine einzige
 * Pruefung im Server anzufassen.
 */
export const CAPABILITIES = [
    "article.read",
    "article.write",
    "article.delete",
    "meta.read",
    "price.input.write",
    "price.parameter.write",
    "review.resolve",
    "supplier.write",
    "import.execute",
    "system.manage",
    "user.manage",
    "text.write",
    "text.release",
    "property.write",
    "property.confirm",
    "model.write",
    "dte.listing",
    // Logistik & Beschaffung V1: es entsteht KEINE neue Rolle, und der Viewer
    // erhaelt keine dieser Faehigkeiten. Rechte fuer Bestellungen, Sendungen
    // oder eine DTE-Einkaufsintegration entstehen hier NICHT -- diese Vorgaenge
    // werden in diesem Block nicht gebaut.
    "logistics.write",
    "logistics.confirm",
    /*
     * Vertrauliche Beschaffungsdaten LESEN (Migration 0015).
     *
     * Getrennt von `logistics.write`, weil Lesen und Schreiben zwei Fragen
     * sind: wer Verpackungsstaende pflegt, muss die Konditionen nicht sehen
     * duerfen. Es ist KEIN Recht auf Logistik -- Zolltarifnummer, Kartonmasse,
     * Gewicht und CBM bleiben mit `article.read` lesbar.
     */
    "purchasing.read",
    /*
     * Einen Listenpreis UNTERHALB der geltenden Mindestmarge freigeben
     * (Migration 0020).
     *
     * Bewusst NICHT an `price.parameter.write` gekoppelt: wer Preise freigibt,
     * tut das im Regelfall innerhalb der Grenze. Die Unterschreitung ist eine
     * andere Entscheidung mit anderer Verantwortung -- und eine Begruendung ist
     * kein Ersatz fuer eine Befugnis.
     */
    "price.minimum_margin.override",
    /*
     * Angebotsvorgaenge erfassen sowie Bestands- und Beschaffungsstaende
     * einspielen (Migration 0025).
     *
     * Es ist KEIN Recht, den Aktivstatus zu setzen -- den gibt es nicht. Der
     * Status wird aus den Tatsachen abgeleitet; dieses Recht erlaubt, die
     * Tatsachen zu erfassen, und nicht, das Ergebnis zu bestimmen.
     */
    "stock.write",
    /*
     * DIE INTERNE MERKMALSPRUEFUNG LESEN (Migration 0031).
     *
     * Bis hierher genuegte `article.read` -- mit der ausdruecklichen Begruendung,
     * eine Warteschlange, die jemand nur SEHEN kann, sei kein Risiko. Die
     * Begruendung verwechselt zwei Fragen. Wer nichts bestaetigen kann, richtet
     * keinen Schaden an; er sieht aber, WORAN gearbeitet wird, WELCHE Werte
     * angezweifelt werden und WIE unsicher der Bestand an einer Stelle ist.
     * Das ist der Arbeitsstand des Hauses, nicht das freigegebene Produktwissen.
     *
     * Die Trennlinie: Produktwissen ja, interner Pruef- und Arbeitsprozess nein.
     *
     * Bewusst getrennt von `property.confirm`: sehen und entscheiden sind zwei
     * Vorgaenge. Und bewusst nicht an `system.manage` gehaengt -- eine Rolle
     * Produktmanagement soll dieses Recht spaeter bekommen koennen, ohne dafuer
     * Administrator zu werden.
     */
    "property.review.read",
    /*
     * DIE UEBERGABE AN NORA HR (Migration 0056).
     *
     * Sie war an kein Recht gebunden: `/internal/handoff` verlangte nur eine
     * Anmeldung, und die Oberflaeche zeigte den Punkt jedem, sobald ein Ziel
     * eingerichtet war. Ein Viewer -- die Rolle fuer rein lesenden, auch
     * externen Zugriff -- bekam damit einen gueltigen Uebergabecode nach
     * Nora HR ausgestellt.
     *
     * Das ist kein Anzeigefehler. HOMEBASE stellt hier einen Ausweis fuer ein
     * ANDERES System aus, und wer ihn bekommt, ist eine Entscheidung ueber
     * Personaldaten.
     *
     * Bewusst NICHT an `system.manage` gehaengt: die Uebergabe ist keine
     * Betriebsfunktion. Fuehrt Nora HR spaeter eigene Rollen, laesst sich
     * dieses eine Recht vergeben, ohne irgendetwas anderes zu oeffnen.
     */
    "hr.handoff",
    /*
     * PRODUKTDOKUMENTE PFLEGEN (Migration 0056).
     *
     * Dokumente hingen an `article.write`. Das war zusammengehoerig gedacht,
     * solange nur Administration und Produktpflege schrieben. Marketing soll
     * Datenblaetter und Medien pflegen und dabei KEINE Artikelstammdaten
     * aendern -- mit `article.write` bekaeme es Nummer, Bezeichnung und
     * Familienzuordnung gleich mit.
     *
     * Die Felder in der Oberflaeche zu verstecken waere kein Rechtemodell,
     * sondern eine Verabredung: ein direkter Aufruf haette sie ausgehebelt.
     *
     * LESEN bleibt bei `article.read` -- ein Datenblatt ist Produktwissen.
     */
    "document.write",
    /*
     * DAS PRODUKTIONS- UND LOGISTIKPORTAL (Migration 0069).
     *
     * Zwei Faehigkeiten, nicht fuenf: getrennte Rechte fuer Termine,
     * Zuordnung und Cutoff waeren Verwaltung ohne Nutzen -- den Bereich
     * bedienen dieselben Menschen. Eine feinere Teilung ist spaeter eine
     * zusaetzliche Faehigkeit und kein Umbau.
     *
     * `procurement.read` ist ausdruecklich NICHT `purchasing.read`: das
     * eine oeffnet einen Arbeitsbereich, das andere schuetzt vertrauliche
     * Beschaffungsangaben am Produkt. Wer Preise sehen darf, muss nicht
     * Bestellungen fuehren duerfen.
     */
    "procurement.read",
    "procurement.write",
];
/** Mindestlaenge fuer Passwoerter. Bewusst laenger als die uebliche Acht. */
export const MIN_PASSWORD_LENGTH = 12;
/** Erlaubte Benutzernamen: klein, ohne Leerzeichen, eindeutig vorlesbar. */
export const USERNAME_PATTERN = /^[a-z][a-z0-9._-]{2,31}$/;
export const API_BASE = "/api/v1";
export const DEFAULT_PAGE_SIZE = 50;
export const MAX_PAGE_SIZE = 500;
/** Zulaessige Sortierspalten. Positivliste -- niemals Benutzereingaben in SQL durchreichen. */
export const SORTABLE_FIELDS = ["article_number", "short_description", "category"];
/**
 * Eine Supplier-Produkt-Beziehung samt ihrer Verpackungsgeschichte.
 *
 * Mehrere Beziehungen je Artikel sind ausdruecklich zulaessig: dieselbe SKU
 * kann bei zwei Suppliern unterschiedliche Verpackung, MOQ und Lieferzeit
 * haben. Genau dafuer existiert diese Ebene.
 */
/**
 * Die vertraulichen Angaben einer Beschaffungsbeziehung.
 *
 * WARUM EIN EIGENES OBJEKT UND NICHT FUENF EINZELFELDER
 *
 * Der Schutz ist damit EINE Grenze statt fuenf. Wer spaeter EXW, EK oder
 * einen Preisstand ergaenzt, legt ihn hier hinein und ist damit automatisch
 * geschuetzt; als flaches Feld daneben waere er es nur, wenn jemand daran
 * denkt. Die Struktur traegt die Regel, nicht die Sorgfalt.
 *
 * Es ist ausserdem eindeutig lesbar: `purchasing === null` heisst „nicht
 * freigegeben", waehrend `moq === null` weiterhin „unbekannt" heisst. Beides
 * in ein `null` zu legen waere derselbe Fehler wie 0 fuer unbekannt.
 */
// ── Das Lieferantenportal ───────────────────────────────────────────────
//
// Der Supplier ist die zentrale Entitaet: seine Angaben stehen EINMAL und
// werden nicht je Artikel wiederholt. Die Produktansicht zeigt sie, sie
// besitzt sie nicht.
/**
 * Der Geschaeftsstatus eines Lieferanten (0042).
 *
 * Er ersetzt das fruehere `active`: vier Zustaende statt eines
 * Schalters. `inactive` ist etwas anderes als ein zusammengefuehrter
 * Datensatz -- der ist kein Lieferant mehr, sondern in einem anderen
 * aufgegangen.
 */
export const SUPPLIER_STATUSES = ["active", "backup", "sourcing", "inactive"];
/** Mit wem heute gearbeitet wird -- abgeleitet, nicht zusaetzlich gepflegt. */
export const SUPPLIER_IN_BUSINESS = ["active", "backup", "sourcing"];
/**
 * Laendercodes nach ISO 3166-1 alpha-2.
 *
 * NUR DIE CODES, keine Namen: die Beschriftung liefert `Intl.DisplayNames`
 * in der Sprache des Benutzers. Eine Liste mit 249 Laendernamen je
 * Sprache im Woerterbuch waere derselbe Inhalt zweimal -- und beim
 * naechsten Land dreimal.
 */
export const COUNTRY_CODES = [
    "AD", "AE", "AF", "AG", "AI", "AL", "AM", "AO", "AQ", "AR", "AS", "AT", "AU", "AW", "AX", "AZ",
    "BA", "BB", "BD", "BE", "BF", "BG", "BH", "BI", "BJ", "BL", "BM", "BN", "BO", "BQ", "BR", "BS",
    "BT", "BV", "BW", "BY", "BZ", "CA", "CC", "CD", "CF", "CG", "CH", "CI", "CK", "CL", "CM", "CN",
    "CO", "CR", "CU", "CV", "CW", "CX", "CY", "CZ", "DE", "DJ", "DK", "DM", "DO", "DZ", "EC", "EE",
    "EG", "EH", "ER", "ES", "ET", "FI", "FJ", "FK", "FM", "FO", "FR", "GA", "GB", "GD", "GE", "GF",
    "GG", "GH", "GI", "GL", "GM", "GN", "GP", "GQ", "GR", "GS", "GT", "GU", "GW", "GY", "HK", "HM",
    "HN", "HR", "HT", "HU", "ID", "IE", "IL", "IM", "IN", "IO", "IQ", "IR", "IS", "IT", "JE", "JM",
    "JO", "JP", "KE", "KG", "KH", "KI", "KM", "KN", "KP", "KR", "KW", "KY", "KZ", "LA", "LB", "LC",
    "LI", "LK", "LR", "LS", "LT", "LU", "LV", "LY", "MA", "MC", "MD", "ME", "MF", "MG", "MH", "MK",
    "ML", "MM", "MN", "MO", "MP", "MQ", "MR", "MS", "MT", "MU", "MV", "MW", "MX", "MY", "MZ", "NA",
    "NC", "NE", "NF", "NG", "NI", "NL", "NO", "NP", "NR", "NU", "NZ", "OM", "PA", "PE", "PF", "PG",
    "PH", "PK", "PL", "PM", "PN", "PR", "PS", "PT", "PW", "PY", "QA", "RE", "RO", "RS", "RU", "RW",
    "SA", "SB", "SC", "SD", "SE", "SG", "SH", "SI", "SJ", "SK", "SL", "SM", "SN", "SO", "SR", "SS",
    "ST", "SV", "SX", "SY", "SZ", "TC", "TD", "TF", "TG", "TH", "TJ", "TK", "TL", "TM", "TN", "TO",
    "TR", "TT", "TV", "TW", "TZ", "UA", "UG", "UM", "US", "UY", "UZ", "VA", "VC", "VE", "VG", "VI",
    "VN", "VU", "WF", "WS", "YE", "YT", "ZA", "ZM", "ZW",
];
/**
 * Zustaendigkeit eines Ansprechpartners. Wertevorrat aus der Datenbank.
 *
 * MEHRERE SIND DER NORMALFALL (0067) -- wer bei einem kleinen Fertiger
 * Sales macht, macht haeufig auch Logistics. Der HAUPTANSPRECHPARTNER
 * steht NICHT in dieser Liste: er ist eine Auszeichnung unter den
 * Kontakten eines Partners und keine Zustaendigkeit.
 */
export const SUPPLIER_CONTACT_ROLES = ["sales", "engineering", "product_development", "quality",
    "logistics", "finance", "management"];
/** Weitere Kontaktkanaele, wie sie in der Praxis vorkommen. */
export const MESSENGER_KINDS = ["wechat", "whatsapp", "skype", "teams"];
/**
 * Ueber welchen Weg dieser Mensch tatsaechlich antwortet.
 *
 * KEIN ZWEITER KONTAKTWEG, sondern ein Verweis auf einen vorhandenen:
 * die Datenbank laesst seit 0067 nur einen Kanal zu, der auch gepflegt
 * ist. „Bevorzugt: Mobil" ohne Mobilnummer waere eine Auskunft, die beim
 * ersten Versuch ins Leere laeuft.
 */
export const CONTACT_CHANNELS = ["email", "phone", "mobile", "messenger"];
/**
 * Die Abschnitte der Lieferantenakte.
 *
 * Dieselbe Mechanik wie bei der Produktakte: die Reihenfolge steht hier,
 * die Beschriftung im Woerterbuch, die Sichtbarkeit an den Faehigkeiten.
 * Ein weiterer Abschnitt ist eine Zeile in dieser Liste.
 */
export const SUPPLIER_SECTIONS = ["uebersicht", "stammdaten", "produkte", "datenbedarf", "konditionen", "finanzen"];
/**
 * WELCHE BEREICHE EINE LIEFERANTENROLLE VERLANGEN.
 *
 * Artikel, Datenbedarf und Konditionen samt Bewertung sind
 * lieferantenspezifisch: ein Forwarder hat keine MOQ, keinen Staffelpreis
 * und keine Lieferantenbewertung. Sie werden deshalb nicht in einen
 * generischen Adressdatensatz geschoben -- sie bleiben, wo sie sind, und
 * erscheinen nur, wo eine Rolle sie rechtfertigt.
 *
 * WELCHE ROLLE das tut, steht nicht hier, sondern in `partner_roles
 * .opens_supplier_areas`. Ein Prueferdienstleister mit eigenen Bereichen
 * ist damit ein Katalogeintrag und keine Codeaenderung.
 *
 * NICHT dabei: Uebersicht und Stammdaten -- jede Organisation hat eine
 * Anschrift und Ansprechpartner. Und Finanzen: eine Bankverbindung
 * braucht auch, wer eine Spedition bezahlt.
 */
export const SUPPLIER_ONLY_SECTIONS = ["produkte", "datenbedarf", "konditionen"];
/**
 * WELCHER BEREICH WELCHES RECHT BRAUCHT -- und warum die Liste leer ist.
 *
 * Heute steht hier nichts: wer die Akte sehen darf (`purchasing.read`),
 * darf alle ihre Bereiche sehen. Die Zuordnung steht trotzdem da, und
 * zwar mit Absicht.
 *
 * FINANZEN UND BANKVERBINDUNGEN sind fachlich vorgesehen und bewusst
 * nicht gebaut. Sie sind die sicherheitskritischste Angabe eines
 * Partners -- geaenderte Kontodaten sind der haeufigste reale
 * Zahlungsschaden -- und brauchen ein EIGENES Recht, ein eigenes
 * Protokoll und eine Bestaetigung durch einen zweiten Menschen. Genau
 * deshalb duerfen sie nicht als weiterer Reiter neben den Stammdaten
 * entstehen, den jeder sieht, der Einkauf lesen darf.
 *
 * Mit dieser Zuordnung ist der Bereich spaeter ZWEI Zeilen: eine in
 * `SUPPLIER_SECTIONS`, eine hier mit seinem Recht. Ohne sie waere es
 * ein Umbau der Leiste -- und die Versuchung gross, ihn ungeschuetzt zu
 * lassen.
 *
 * KEIN LEERER REITER ENTSTEHT DADURCH. Ein Bereich, der nichts kann,
 * ist ein Versprechen; diese Liste legt keinen an.
 */
export const SUPPLIER_SECTION_CAPABILITY = {
    /*
     * FINANZEN sind normale interne Supplier-Daten (Implementierung 003).
     * Kein eigenes Recht, kein Vier-Augen-Verfahren -- gelesen wird wie die
     * uebrige Akte, geschrieben mit `supplier.write`.
     *
     * Der Eintrag steht trotzdem hier und nicht als stillschweigende
     * Annahme: er sagt ausdruecklich, WELCHES Recht diesen Bereich traegt.
     * Faellt die Entscheidung spaeter anders aus, ist es eine Zeile.
     */
    finanzen: "purchasing.read",
};
/**
 * Die fuenf Kriterien -- benannt und fachlich entschieden.
 *
 * Nicht frei erweiterbar: wer ein sechstes will, entscheidet es fachlich
 * und bringt eine Spalte mit. Diese Liste ist die EINE Quelle -- Server,
 * Oberflaeche und Pruefstand zaehlen aus ihr und nicht jeder fuer sich.
 */
export const SUPPLIER_RATING_CRITERIA = ["production", "reliability", "communication", "price", "paymentTerms"];
/* ── Zollstammdaten (Migration 0080) ────────────────────────────────────── */
/**
 * Die Zollinformationen, die HOMEBASE je Artikel fuehrt.
 *
 * Jede ist ein eigener Fakt mit eigener Herkunft, eigener Belastbarkeit
 * und eigener Unterschrift. Das Ursprungsland steht auf der Proforma des
 * Lieferanten; das Nettogewicht hat vielleicht jemand im Haus gewogen.
 * Sie gemeinsam zu qualifizieren hiesse, eine Korrektur am Gewicht die
 * Unterschrift unter dem Ursprungsland mitreissen zu lassen.
 */
export const CUSTOMS_FACT_CODES = ["origin_country", "net_weight_kg"];
/*
 * ═══════════════════════════════════════════════════════════════════════════
 * PRODUKTDOKUMENTE (Migration 0052)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Fuenf feste Faecher, immer alle fuenf sichtbar. Ein leeres Fach ist eine
 * Aussage; eine leere Seite ist keine.
 *
 * Die Gruppen stehen hier als Konstante UND in `document_groups`. Das ist
 * kein Versehen: die Datenbank braucht die Ordnung fuer die Abfrage, die
 * Oberflaeche braucht den Typ, um jeder Gruppe eine Beschriftung zuordnen zu
 * koennen, ohne eine Liste zu erfinden. Wer eine sechste Gruppe einfuehrt,
 * aendert beide Stellen -- und genau das soll auffallen.
 */
export const DOCUMENT_GROUPS = [
    "lichtplanung_ies",
    "produktdatenblatt",
    "led_treiber_datenblatt",
    "zertifikate_konformitaet",
    "produktanleitung",
    /*
     * Pruefberichte und sonstige Dateien (0057). Mess- und Testberichte lagen
     * mangels passenden Fachs unter „Lichtplanung / IES". Vorhandene Dateien
     * werden nicht umsortiert -- ob ein PDF ein Pruefbericht ist, entscheidet
     * ein Mensch, nicht ein Dateiname.
     */
    "pruefberichte_sonstiges",
];
/**
 * DIE DREI DOKUMENTE EINER BESTELLUNG -- in der Reihenfolge des Prozesses.
 *
 * Sie steht hier und nicht in der Oberflaeche, weil beide Seiten
 * dieselbe Reihenfolge brauchen: die Akte zeigt sie so, und die
 * Vollstaendigkeitsauskunft zaehlt sie so.
 */
export const BESTELLDOKUMENTE = ["bestellung_po", "proforma_pi", "packing_list"];
/**
 * Das Fach des Zahlungsnachweises (0090).
 *
 * Es steht NICHT in `BESTELLDOKUMENTE`: jene drei bilden die
 * dokumentarische Wahrheit der Bestellung und werden als Faecher
 * gezeigt. Ein Zahlungsnachweis gehoert zu einer ZAHLUNG und
 * erscheint dort -- nicht als vierte Zeile neben PO, PI und Packing
 * List.
 */
export const ZAHLUNGSNACHWEIS = "zahlungsnachweis";
/**
 * IST DIESES DOKUMENT SCHON FAELLIG?
 *
 * DER BEFUND DER BROWSER-ABNAHME (Abschnitt 18). Die Dokumentkarte
 * sagte an einer frisch angelegten Bestellung dreimal „fehlt" --
 * Purchase Order, Proforma Invoice, Packing List. Zur selben Zeit
 * sagte die Prozessfuehrung ueber dieselbe Bestellung, es sei alles
 * in Ordnung. Zwei Anzeigen, zwei Aussagen, ein Vorgang.
 *
 * DER GRUNDSATZ: „FEHLT" DARF NUR HEISSEN, DASS ETWAS ERFORDERLICHES
 * NICHT DA IST. Ein Dokument, das im Ablauf noch gar nicht an der
 * Reihe ist, fehlt nicht -- es ist noch nicht dran. Wer beides gleich
 * faerbt, bringt dem Leser bei, die Warnung zu uebersehen; und dann
 * uebersieht er sie auch, wenn sie stimmt.
 *
 *   erforderlich            Der Ablauf verlangt es JETZT.
 *   noch_nicht_erforderlich Es gehoert zu einem spaeteren Schritt.
 *
 * ABGELEITET, NICHT GEPFLEGT. Es gibt kein Feld „Pflicht" an einem
 * Fach: die Faelligkeit ergibt sich aus dem Stand des Vorgangs, und
 * eine Kopie davon gaebe irgendwann die andere Antwort.
 *
 * EIN DRITTER ZUSTAND FEHLT HIER BEWUSST. Abschnitt 18.3 nennt auch
 * „nicht erforderlich" -- fuer einen Vorgang, der fachlich keine
 * Packliste braucht. Dazu gibt es in HOMEBASE keine Tatsache: niemand
 * hat diese Entscheidung je festgehalten. Sie zu RATEN waere
 * schlimmer als sie nicht anzuzeigen. Der Weg dorthin ist bekannt und
 * steht im Memo zur Rueckgabe -- ein Termincode wie `qc_not_required`
 * aus 0084, der die Entscheidung mit Datum und Person festhaelt.
 */
export const DOCUMENT_OBLIGATIONS = ["erforderlich", "noch_nicht_erforderlich"];
/**
 * Die Groessengrenze -- 20 MB.
 *
 * Steht hier, weil beide Seiten sie brauchen: die Oberflaeche, um eine zu
 * grosse Datei gar nicht erst hochzuladen, und der Server, um sie
 * abzuweisen. Die letzte Schranke ist keine von beiden, sondern
 * `pd_size_chk` in der Datenbank.
 */
export const MAX_DOCUMENT_BYTES = 20 * 1024 * 1024;
/**
 * Welche Dateiendungen abgelegt werden duerfen -- und als welcher Inhaltstyp
 * sie ausgeliefert werden.
 *
 * Der vom Browser gemeldete Typ wird NICHT uebernommen. Er ist frei setzbar,
 * und ein uebernommenes `text/html` waere eine Seite auf eigener Herkunft,
 * ausgefuehrt mit der Sitzung des Lesers. Die Endung ist ebenfalls frei
 * setzbar -- aber sie entscheidet hier nur, ob die Datei UEBERHAUPT
 * angenommen wird, und bildet auf einen Typ aus einer geschlossenen Liste ab.
 *
 * Eine unbekannte Endung wird abgewiesen statt als `application/octet-stream`
 * abgelegt. Eine Datei, deren Art niemand kennt, laesst sich nicht sicher
 * ausliefern -- und ein Auffangtyp waere die Tuer, durch die alles passt.
 */
export const DOCUMENT_FILE_TYPES = {
    pdf: "application/pdf",
    /* Photometrie: IESNA LM-63 (.ies) und EULUMDAT (.ldt) sind Textdateien. */
    ies: "text/plain; charset=utf-8",
    ldt: "text/plain; charset=utf-8",
    txt: "text/plain; charset=utf-8",
    zip: "application/zip",
    png: "image/png",
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
};
/* ── Produktions- und Logistikportal (Stage 1A) ──────────────────────────── */
/**
 * DREI ACHSEN, DIE NIE VERSCHMOLZEN WERDEN.
 *
 *   WAS BESTELLT WURDE   PurchaseOrder -> PurchaseOrderLine
 *   WAS ZUSAMMEN REIST   Consolidation -> ConsolidationOrder
 *   WAS WANN GESCHIEHT   Milestone, Forecast, TimelineEvent
 *
 * Die Zeitachse ist eine SICHT auf diese drei. Sie besitzt keinen
 * einzigen Termin selbst.
 */
/** Der Geschaeftsstatus einer Bestellung -- eine Entscheidung, kein Fortschritt. */
export const PURCHASE_ORDER_STATUSES = ["offen", "geschlossen", "storniert"];
/**
 * Der Lifecycle einer Consolidation.
 *
 * ABGELEITET, NICHT GEPFLEGT -- mit zwei benannten Ausnahmen.
 *
 *   geplant        Anlage
 *   gebucht        `freight_ordered` als Ist
 *   in_transport   `departed` als Ist -- die tatsaechliche Abfahrt, seit
 *                  0086. Bis dahin trug die Carrier-Uebergabe diesen
 *                  Uebergang, weil es keine Abfahrt gab; zwischen beidem
 *                  liegen bei Seefracht aber Tage.
 *   zugestellt     `goods_delivered` als Ist
 *   geschlossen    bewusster Abschluss nach der Zustellung
 *   verworfen      bewusster Abbruch vor der Carrier-Uebergabe
 *
 * Die ersten vier entstehen aus Ereignissen; sie von Hand zu setzen waere
 * eine zweite Wahrheit ueber denselben Vorgang. Die letzten beiden sind
 * Verwaltungsentscheidungen -- zu ihnen gibt es kein Ereignis, dem sie
 * widersprechen koennten.
 *
 * ETD und ETA sind PLANWERTE und aendern den Zustand NIE. Eine
 * verschobene Ankunft ist eine Information, keine Tatsache ueber den Ort
 * der Ware.
 */
/**
 * DAS KONSOLIDIERUNGSFENSTER -- abgeleitet, nicht gepflegt.
 *
 * Es sagt, ob der INHALT dieser Sendung noch geaendert werden kann --
 * ob also eine Bestellung beitreten oder herausgenommen werden kann.
 *
 *   offen                   bis zur Carrier-Uebergabe
 *   geschlossen             ab der Carrier-Uebergabe
 *   geschlossen_altvorgang  keine Uebergabe dokumentiert, aber ein
 *                           SPAETERES Ist beweist, dass die
 *                           Konsolidierung vorbei ist
 *
 * BERICHTIGT GEGENUEBER 002: dort haing das Fenster an `departed`. Das
 * war zu spaet -- zwischen Uebergabe und Ablegen liegen Tage, in denen
 * die Oberflaeche ein Hinzufuegen angeboten haette, das die Datenbank
 * abweist. `departed` bleibt unveraendert die tatsaechliche Abfahrt
 * und der Uebergang nach IN TRANSPORT; die beiden Fragen werden nur
 * nicht mehr verwechselt.
 *
 * ES GIBT DAZU KEIN FELD. Kein manuelles Schliessen, kein
 * Wiederoeffnen, keine eigene Historie, keine eigenen Rechte -- was sich
 * aus einem vorhandenen Ereignis eindeutig ergibt, wird nicht
 * zusaetzlich gespeichert, weil die Kopie sonst irgendwann die andere
 * Antwort gibt.
 *
 * UND DER CUT-OFF SCHLIESST NICHTS. Er ist ein Planwert. Ein Datum, das
 * sich um Mitternacht selbst vollstreckt, haette CN-01 zugemacht,
 * waehrend die letzte Bestellung noch unterwegs zum Logistikpartner war.
 *
 * HEISST INTERN `ConsolidationIntakeState` UND NICHT
 * `ConsolidationWindow`: diesen Namen traegt seit 0070 die Frage, ob
 * eine EINZELNE Bestellung zeitlich noch in eine Sendung passt. Zwei
 * Dinge mit einem Namen sind beim Lesen eine Falle.
 */
export const CONSOLIDATION_INTAKE_STATES = ["offen", "geschlossen", "geschlossen_altvorgang"];
export const CONSOLIDATION_STATUSES = ["geplant", "gebucht", "in_transport", "zugestellt", "geschlossen", "verworfen"];
/**
 * Wie eine Sendung reist -- Katalog `transport_modes` (Migration 0079).
 *
 * NICHT zu verwechseln mit `TRANSPORT_MODES` in `domain.ts`. Jene Liste
 * sagt, auf welcher FRACHTRATE eine Kalkulation gerechnet hat; diese
 * sagt, wie die Ware TATSAECHLICH reist. Beide fallen oft zusammen und
 * muessen es nicht -- fuer Strasse und Multimodal gibt es keinen
 * Frachtsatz, und eine Kalkulation gibt es auch ohne Sendung.
 *
 * `train` und nicht `rail`: die Bahn heisst im Haus seit 0055 so.
 */
export const CONSOLIDATION_TRANSPORT_MODES = ["sea", "air", "train", "road", "multimodal", "other"];
/**
 * Die Sicherheit eines Termins -- und die Rangfolge zwischen ihnen.
 *
 * GESPEICHERT werden nur `confirmed` und `actual`. Ein `forecast` wird
 * bei jedem Lesen gerechnet und nie abgelegt: waere er speicherbar,
 * waere er von einer Zusage nicht mehr zu unterscheiden.
 */
export const MILESTONE_CERTAINTIES = ["forecast", "confirmed", "actual"];
/** Was gespeichert werden darf. Die Datenbank haelt dieselbe Grenze. */
export const STORED_CERTAINTIES = ["confirmed", "actual"];
export const MILESTONE_SUBJECTS = ["purchase_order", "consolidation"];
/** Woher eine Lieferzeitregel stammt. Eine Annahme ist keine Zusage. */
export const LEAD_TIME_SOURCES = ["supplier_pi", "supplier_statement", "internal_assumption"];
/** Die Meilensteincodes der Stufe 1A. Reihenfolge und Regeln aus 0069. */
export const MILESTONE_CODES = [
    "po_ordered", "pi_received", "pi_approved", "deposit_due", "deposit_paid",
    "artwork_sent", "artwork_approved", "production_start", "production_ready",
    "qc_planned", "qc_passed",
    /*
     * QC NICHT ERFORDERLICH (0084) -- eine ENTSCHEIDUNG, keine Pruefung.
     *
     * Sie steht neben den beiden Pruefterminen und nicht an ihrer Stelle:
     * wer sie erfasst, sagt, dass fuer diese Bestellung bewusst nicht
     * geprueft wird. Das ist etwas anderes als bestanden -- und etwas
     * anderes als noch offen.
     *
     * (Ohne Anfuehrungszeichen, siehe die Warnung am Kopf dieser Liste:
     * checki18n C35 liest die Codes aus ihren Zeichenketten.)
     */
    "qc_not_required", "ready_for_shipment",
    "consolidation_cutoff", "consolidation_ready", "planned_etd",
    /*
     * Die Kette bis zum Wareneingang (0074).
     *
     * Ankunft Hamburg, Verzollung und Ankunft Lueneburg gelten fuer die
     * SENDUNG und haengen deshalb an der Consolidation. ETA Hamburg und
     * ETA Lueneburg sind ZWEI Termine -- zwischen ihnen liegen Verzollung
     * und Weitertransport.
     *
     * Der Wareneingang haengt an der BESTELLUNG: er ist die Erfuellung
     * dieses Auftrags.
     */
    "eta_hamburg", "customs_cleared", "eta_lueneburg", "goods_received",
    /*
     * Der Logistikablauf (0083).
     *
     * Die Uebergabe an den CARRIER ist der Transportbeginn und etwas
     * anderes als der Eingang beim Logistikpartner: der steht je
     * Bestellung an der Zuordnung, weil er eine Aussage ueber EINE
     * Bestellung in DIESER Sendung ist.
     *
     * Die tatsaechliche Anlieferung bei greenius steht neben dem Avis --
     * dieselbe Trennung, die 0077 fuer Hamburg gemacht hat und die fuer
     * Lueneburg nachgezogen wurde.
     */
    "carrier_handover", "goods_delivered",
    /*
     * Abfahrt und Hamburger Uebergabe (0086).
     *
     * `departed` ist die TATSAECHLICHE Abfahrt am Ausgangsort und etwas
     * anderes als `carrier_handover`: die Ware kann beim Carrier liegen,
     * ohne dass etwas abgefahren ist. 0083 hatte diesen Termin bereits
     * vorgemerkt und ausdruecklich nicht vorsorglich gebaut.
     *
     * `hh_handover` ist die Uebergabe an den WEITERFUEHRENDEN
     * Logistikpartner in Hamburg -- nicht zu verwechseln mit dem
     * abgekuendigten `goods_handed_over`, das den FERNOST-Partner meinte.
     */
    "departed", "hh_handover",
    /*
     * Packliste und Frachtkette (0075).
     *
     * An der BESTELLUNG fehlte genau ein Ereignis: die Packliste. Sie ist
     * ein Dokument, das ankommt -- wie die Proforma -- und steht hinter der
     * Versandbereitschaft, weil eine Packliste entsteht, wenn gepackt ist.
     *
     * An der CONSOLIDATION fehlte alles VOR der Abfahrt. Anfragen, ein
     * Angebot bekommen, beauftragen, uebergeben: vier Tatsachen, nicht ein
     * Vorgang mit vier Namen -- zwischen Angebot und Auftrag liegt eine
     * Entscheidung, zwischen Auftrag und Uebergabe die Abholung.
     *
     * `customs_docs_sent` ist die UEBERMITTLUNG der Papiere und nicht die
     * Verzollung. Die steht als `customs_cleared` daneben; zwischen beiden
     * liegt die Arbeit des Zolls.
     */
    "packing_list_received",
    "freight_requested", "freight_quoted", "freight_ordered",
    "goods_handed_over", "customs_docs_sent",
    /*
     * Die erwartete Ankunft und die tatsaechliche sind zwei Termine (0077).
     *
     * `eta_hamburg` heisst in der Anzeige ETA HH und ist die ERWARTUNG --
     * sie verschiebt sich, und jede Verschiebung ist eine Information.
     * `hh_landed` ist das Ereignis: das Schiff ist da. Beides in einem Code
     * zu fuehren hiesse, dass eine verschobene Erwartung und eine
     * eingetroffene Tatsache einander abloesen -- danach waere nicht mehr
     * zu beantworten, wie oft verschoben wurde und wann das Schiff kam.
     *
     * KEINE ANFUEHRUNGSZEICHEN IN DIESEM BLOCK: `checki18n` C35 liest die
     * Codes ueber die Zeichenketten dieser Liste und zaehlte einen
     * Kommentar sonst mit.
     */
    "hh_landed",
];
/**
 * Die Anlage traegt VEREINBARUNGSDATEN -- und keine Prozessdaten.
 *
 * Nummer, Lieferant, Belegdatum, Lieferklausel, Waehrung, Notiz: das
 * steht auf der Bestellung. PI, Anzahlung, QC und Versandbereitschaft
 * sind Termine und werden dort erfasst, wo sie geschehen -- ein
 * Anlageformular, das nach ihnen fragt, verleitet dazu, sie zu erfinden.
 */
/** Wie eine Bestellung in HOMEBASE entstanden ist. */
export const CAPTURE_MODES = ["manual", "upload"];
/* ── Kaufmaennische Sicht: Zahlungen und offene Verpflichtungen ─────────── */
/**
 * WAS EINE GELEISTETE ZAHLUNG IST.
 *
 * Nicht zu verwechseln mit `payment_events` -- die sagen, WANN eine Rate
 * faellig wird (nach QC, vor B/L, 45 Tage nach Rechnung). Das ist die
 * Ausloesebedingung einer VEREINBARUNG. Hier steht, was eine tatsaechlich
 * geflossene Zahlung war.
 */
/**
 * WIE gezahlt wird -- der Weg, nicht der Anlass.
 *
 * AUSDRUECKLICH NICHT ZU VERWECHSELN mit `PaymentTerms.method`. Jenes ist
 * die ERFASSUNGSART eines Plans (erfasst, uebernommen, extrahiert,
 * abgeleitet) und sagt, woher HOMEBASE die Abrede kennt. Dieses hier sagt,
 * ueber welchen Weg das Geld geflossen ist. Beide heissen im Alltag
 * „Methode"; sie haben nichts miteinander zu tun.
 *
 * Deshalb traegt jedes Feld dieses Typs den Namen `paymentMethod`, nie
 * `method` -- der Tippfehler, der beide verwechselt, soll nicht
 * uebersetzen.
 */
export const PAYMENT_METHODS = [
    "bank_transfer", "letter_of_credit", "paypal", "credit_card", "cash", "other",
];
export const PAYMENT_KINDS = ["deposit", "partial", "balance", "other"];
/**
 * WAS AN DER KAUFMAENNISCHEN SICHT NICHT AUFGEHT.
 *
 * Sprachneutrale Codes; die Saetze stehen im Woerterbuch. Keiner davon
 * ist eine steuerliche Einordnung -- sie sagen, wo die Zahlen einander
 * widersprechen, und sonst nichts.
 */
export const FINANCE_FINDINGS = [
    "ueberzahlt", "fremdwaehrung", "kein_bestellwert", "keine_waehrung",
    "zahlung_vor_bestellung", "bezahlt_ohne_wareneingang", "ware_da_rest_offen",
    /*
     * Der Bestellwert des Stichtags liess sich nicht zurueckrechnen.
     *
     * Der Fall: eine Position wurde entfernt, bevor HOMEBASE dabei Menge
     * und Preis protokollierte. Dann bleibt `orderValue` LEER -- geraten
     * wird nicht.
     */
    "wert_nicht_rekonstruierbar",
    /*
     * DER BESTELLWERT IST UNVOLLSTAENDIG -- es gibt Positionen ohne Preis.
     *
     * DER BEFUND AUS DER PRAXIS (PO70305). Die Summe addierte die
     * bepreisten Positionen und sah vollstaendig aus. Eine Zahl, die
     * vollstaendig aussieht und es nicht ist, ist schlimmer als keine:
     * niemand prueft nach, was ihn nicht stutzig macht.
     *
     * KEIN FEHLER, SONDERN EIN ZUSTAND. Eine Bestellung darf Positionen
     * ohne Preis fuehren -- Muster, Beistellteile, noch nicht
     * verhandeltes. Gesagt werden muss es trotzdem.
     */
    "bestellwert_unvollstaendig",
];
/**
 * Wo sich eine Bestellung im Prozess befindet -- ABGELEITET.
 *
 * Keine zweite Statusmaschine: der Stand ergibt sich aus den erfassten
 * Terminen. `code` ist der weiteste Termin, der eingetreten ist.
 */
export const PROCESS_STAGES = [
    "bestellt", "pi", "produktion", "qc", "ready", "konsolidierung",
    "unterwegs", "hamburg", "verzollt", "lueneburg", "wareneingang",
];
/* ── Die Erfassung einer Bestellung aus einem Beleg ──────────────────────── */
/**
 * WAS AN EINEM VORSCHLAG NICHT SICHER IST.
 *
 * Sprachneutrale Codes; die Saetze stehen im Woerterbuch. Ein Befund ist
 * keine Fehlermeldung -- er ist die Auskunft, wo ein Mensch hinsehen
 * muss, bevor er bestaetigt.
 */
export const BELEG_BEFUNDE = [
    "kein_text", "verschluesselt", "keine_nummer", "kein_datum", "kein_lieferant",
    "keine_waehrung", "kein_gesamtwert", "keine_positionen", "keine_tabelle",
    "summe_weicht_ab",
];
export const POSITIONS_BEFUNDE = [
    "menge_fehlt", "menge_abgeleitet", "preis_fehlt", "wert_fehlt",
    "rechnung_geht_nicht_auf", "keine_artikelnummer",
];
/**
 * Eine Berechnungsgrundlage fuer einen Forecast -- KEINE Terminzusage.
 *
 * Dauer UND Bezugspunkt: „5-6 Wochen nach Anzahlung" ist etwas anderes
 * als „30-60 Tage ab Bestellung", und der Unterschied entscheidet, ob
 * ein Forecast stimmt.
 */
/**
 * IN WELCHEM RAUM DER BEZUGSPUNKT EINER LIEFERZEITREGEL LIEGT (0092).
 *
 * DIE LIEFERZEITREGEL BEANTWORTET: ab welchem Ereignis beginnt die Lead
 * Time? Arnes Entscheidung nennt drei Bezugspunkte -- „PI freigegeben"
 * ist ein Termin, „Anzahlung geleistet" und „Vollstaendig bezahlt" sind
 * ZAHLUNGEN.
 *
 * ZWEI RAEUME, WEIL ES ZWEI WAHRHEITEN SIND. Seit 0074/0078 steht, was
 * tatsaechlich bezahlt wurde, in `purchase_order_payments` -- und die
 * Anzahlungstermine sind ausdruecklich abgekuendigt. Sie als Termin
 * zurueckzuholen hiesse, eine Kopie der Zahlungen im Terminwerk
 * anzulegen, die irgendwann die andere Antwort gibt. Die Regel zeigt
 * deshalb in den Zahlungsraum, statt ihn nachzubauen.
 */
export const LEAD_TIME_ANCHOR_KINDS = ["milestone", "payment"];
/**
 * Der Bezugspunkt im Zahlungsraum.
 *
 *   deposit_paid   die Anzahlung -- die frueheste Zahlung der Art
 *                  „Anzahlung" an dieser Bestellung
 *   paid_in_full   die Zahlung, mit der die Summe den Bestellwert
 *                  ERREICHT -- gerechnet, nie erfasst
 *
 * `paid_in_full` IST NICHT IMMER BESTIMMBAR. Traegt eine Position keinen
 * Preis, ist der Bestellwert unvollstaendig, und dann gibt es keinen
 * Punkt, an dem er erreicht waere. Dann entsteht KEIN Datum und kein
 * Forecast -- siehe `LeadTimeRuleUse.anchorGap`.
 */
export const PAYMENT_ANCHORS = ["deposit_paid", "paid_in_full"];
/**
 * WARUM KEIN BEZUGSDATUM DASTEHT -- oder `null`, wenn eines dasteht.
 *
 * „Kein Forecast" ist eine Auskunft und keine fehlende Zeile. Ohne
 * Grund liest sie sich wie ein Fehler; mit Grund ist sie der Hinweis,
 * was zu tun ist.
 *
 *   kein_bezugsdatum           Das Ereignis ist noch nicht eingetreten
 *                              bzw. die Zahlung noch nicht erfasst.
 *   bestellwert_unvollstaendig Bei `paid_in_full`: eine Position traegt
 *                              keinen Preis, also gibt es keinen Punkt,
 *                              an dem der Wert erreicht waere. Ein
 *                              Datum zu nennen hiesse hier, eine Summe
 *                              zu erfinden.
 */
export const LEAD_TIME_ANCHOR_GAPS = ["kein_bezugsdatum", "bestellwert_unvollstaendig"];
/**
 * Passt eine Bestellung zeitlich noch in eine Consolidation?
 *
 * Gefuehrt wird der KONSERVATIVE Puffer. Wer mit dem optimistischen
 * plant, plant mit dem besten Fall -- und der ist bei einer Spanne von
 * 30 bis 60 Tagen die Ausnahme.
 */
export const WINDOW_STATES = ["passt", "passt_knapp", "passt_nicht", "unbestimmt"];
/**
 * ══════════════════════════════════════════════════════════════════════
 * DIE CHINA-PLANUNGSEBENE (0093)
 * ══════════════════════════════════════════════════════════════════════
 *
 * Die gesamte relevante Beschaffung von greenius laeuft ueber China.
 * Chinesische Feiertage und ihre realen Vor- und Nachwirkungen
 * beeinflussen Kommunikation, Produktion, Freigaben und Versand. Wer im
 * Oktober plant, muss sehen, dass das Fruehlingsfest des Folgejahres
 * mitten in die Produktion faellt.
 *
 * ES IST EINE INFORMATIONSEBENE UND KEINE WARNUNG. Ein China-Ereignis
 * gehoert zu keiner Bestellung und zu keiner Sendung, es veraendert
 * KEINE Lieferzeit, keinen Forecast und keinen Termin. Es sagt nur, was
 * in diesem Zeitraum ohnehin gilt.
 */
export const CHINA_SOURCE_STATES = ["bestaetigt", "vorlaeufig"];
//# sourceMappingURL=api.js.map